import type { AlternativeDataSource } from '../SessionService';

import { getAuthData } from '../AuthService';
import { _sendReply } from '../Communication';
import { registerHandler } from '../MessageManager';

import { PCON_LOGIN_CLIENT_ID } from '@egr/xbox/pcon-login-api/Constants';
import { getLicenseInfo } from '@egr/xbox/pcon-login-api/Licsense';
import { pConServiceProvider } from '@egr/xbox/pcon-login-api/ServicesProvider';
import { UrlVars, getEnvVar } from '@egr/xbox/utils/ReactScriptHelper';
import { buildQueryString } from '@egr/xbox/utils/Url';

import { isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';
import { GatekeeperClient } from '@easterngraphics/gatekeeper-lib';

const gatekeeperClient = new GatekeeperClient({
    serviceProvider: pConServiceProvider,
});

async function getDataSetMetaData(): Promise<Record<string, {name: string, projectSettings?: Record<string, unknown>}>> {
    try {
        return gatekeeperClient.getSettings('pCon_facts', 'multiple_ofml_data_sets');
    } catch {
        return {};
    }
}

export async function requestDataSources(token: string | undefined): Promise<Array<Omit<AlternativeDataSource, 'active'>>> {
    if (token != null && isNotNullOrEmpty(PCON_LOGIN_CLIENT_ID)) {
        const response = await getLicenseInfo(token, PCON_LOGIN_CLIENT_ID);
        const dataSetValue = response?.feature_properties?.['egr.pfx.multiple_ofml_data_sets']?.data_sets as string | undefined;
        if (dataSetValue != null) {
            const metaData = await getDataSetMetaData();

            // Note: there are two `map` calls because it simplifies the `type` definition and it should not
            // effect the performance in a meaningful way
            return dataSetValue.split(',').map(v => v.trim()).filter(isNotNullOrEmpty).map((value) => {
                return {
                    id: value,
                    name: metaData[value]?.name ?? value,
                    projectSettings: metaData[value]?.projectSettings
                };
            });
        }
    }

    return [];
}

async function getAlternativeDataSources(): Promise<ReadonlyArray<AlternativeDataSource>> {
    const authData = await getAuthData();
    const dataSources = await requestDataSources(authData.access_token);
    const gatekeeperId = getEnvVar('GATEKEEPER_ID', '');
    return dataSources.map((dataSource) => {
        return {
            ...dataSource,
            active: dataSource.id === gatekeeperId
        };
    });
}

export function registerDebugSessionService(): void {
    registerHandler({
        type: 'session_service',
        callback: async (event: MessageEvent): Promise<void> => {
            try {
                switch (event.data.action) {
                    case 'get_alternative_data_sources':
                        _sendReply(event.data.transaction_id, false, await getAlternativeDataSources());
                        break;
                    case 'set_alternative_data_sources':
                        const id = event.data.arguments;
                        if (isNotNullOrEmpty(id)) {
                            window.location.hash = buildQueryString({
                                ...UrlVars,
                                GATEKEEPER_ID: id
                            });
                            window.location.reload();
                        }
                        break;
                    default:
                        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                        _sendReply(event.data.transaction_id, true, 'invalid action: ' + event.data.action);
                }
            } catch (error) {
                _sendReply(event.data.transaction_id, true, error instanceof Error ? error.message : error);
            }
        }
    });
}